import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarPlus, ChevronLeft, ChevronRight, Syringe } from "lucide-react";
import { PageHeader } from "../../shared/ui/PageHeader";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { appointmentStatusLabel } from "../../shared/format";
import {
  vaccinationApi,
  vaxBookingStatusLabel,
} from "../../shared/api/vaccination";
import { Button } from "@/components/ui/button";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useOperatesModule } from "../../shared/hooks/useOperatesModule";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";

function todayIso(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Ho_Chi_Minh" });
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

type Row = {
  kind: "EXAM" | "VAX";
  time: string | null; // HH:mm, null = no fixed time (vaccination)
  patientName: string;
  detail: string;
  statusLabel: string;
  statusVariant: BadgeVariant;
  href: string | null;
};

const APPT_STATUS_VARIANT: Record<string, BadgeVariant> = {
  REQUESTED: "warning",
  CONFIRMED: "secondary",
  CHECKED_IN: "secondary",
  COMPLETED: "success",
  NO_SHOW: "muted",
  CANCELLED: "muted",
};
const PAGE_SIZE = 20;

const VAX_STATUS_VARIANT: Record<string, BadgeVariant> = {
  REQUESTED: "warning",
  APPROVED: "secondary",
  SCHEDULED: "secondary",
  FULFILLED: "success",
  CANCELLED: "muted",
};

/**
 * Lịch trong ngày — a single reception agenda that merges doctor exams
 * (appointments) with vaccination bookings (vaccine_request). The two live in
 * separate tables by design; this view just reads both and shows them together,
 * tagged by type. Exams sort by their time slot; vaccinations have no fixed time.
 */
type TypeFilter = "ALL" | "EXAM" | "VAX";

export function UnifiedAgendaPage() {
  // Ngày, loại lịch và trang nằm trên URL (mặc định hôm nay, tất cả, trang 1); đổi ngày hay loại
  // thì về trang đầu.
  const { filters, set } = useUrlFilters({
    date: param.isoDate(todayIso),
    type: param.oneOf(["ALL", "EXAM", "VAX"] as const, "ALL"),
    page: param.int(1, 1),
  });
  const { date, type: typeFilter, page } = filters;
  const setDate = (d: string) => set({ date: d });

  const exams = useQuery({
    queryKey: appointmentQueryKeys.forDate(date),
    queryFn: () => appointmentsApi.forDate(date),
  });
  // Lịch tiêm chỉ có khi phòng khám dùng vùng Tiêm chủng (ADR-0048, ADR-0053). Không dùng thì
  // lịch trong ngày chỉ gồm lịch khám, thay vì cả trang báo lỗi vì một nửa nó không có.
  const vaccination = useOperatesModule("VACCINATION");
  const vax = useQuery({
    queryKey: ["vaccination", "bookings", date, ""],
    queryFn: () => vaccinationApi.listBookings({ date }),
    enabled: vaccination.loaded && vaccination.operates,
  });

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const a of exams.data ?? []) {
      out.push({
        kind: "EXAM",
        time: a.startAt ? a.startAt.slice(11, 16) : null,
        patientName: a.patientName,
        detail: [a.serviceName, a.doctorName].filter(Boolean).join(" · ") || "Khám",
        statusLabel: appointmentStatusLabel[a.status] ?? a.status,
        statusVariant: APPT_STATUS_VARIANT[a.status] ?? "outline",
        href: `/appointments/${a.id}`,
      });
    }
    for (const b of vax.data ?? []) {
      out.push({
        kind: "VAX",
        time: null,
        patientName: b.patientName,
        detail: b.vaccineName || b.protocolName || "Tiêm chủng",
        statusLabel: vaxBookingStatusLabel[b.status] ?? b.status,
        statusVariant: VAX_STATUS_VARIANT[b.status] ?? "outline",
        href: "/vaccination/booking",
      });
    }
    // Exams by time first, then untimed vaccinations.
    out.sort((x, y) => {
      if (x.time && y.time) return x.time.localeCompare(y.time);
      if (x.time) return -1;
      if (y.time) return 1;
      return 0;
    });
    return out;
  }, [exams.data, vax.data]);

  function shiftDay(delta: number) {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toLocaleDateString("en-CA"));
  }

  const visibleRows = useMemo(
    () => (typeFilter === "ALL" ? rows : rows.filter((r) => r.kind === typeFilter)),
    [rows, typeFilter],
  );

  // Pagination — 20 rows/page. currentPage is clamped so the slice is never out of range.
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => visibleRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [visibleRows, currentPage],
  );

  const examCount = exams.data?.length ?? 0;
  const vaxCount = vax.data?.length ?? 0;
  const loading = exams.isLoading || vax.isLoading;

  const emptyLabel =
    typeFilter === "EXAM"
      ? "Không có lịch khám nào trong ngày."
      : typeFilter === "VAX"
        ? "Không có lịch tiêm nào trong ngày."
        : "Không có lịch nào trong ngày.";

  return (
    <div>
      <PageHeader
        title="Lịch trong ngày"
        description="Gộp lịch khám (bác sĩ) và lịch tiêm chủng của cả phòng khám trong một ngày."
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/appointments/new">
                <CalendarPlus />
                Đặt lịch khám
              </Link>
            </Button>
            <Button asChild>
              <Link to="/vaccination/booking">
                <Syringe />
                Đặt lịch tiêm
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => shiftDay(-1)}
          aria-label="Ngày trước"
        >
          <ChevronLeft />
        </Button>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-auto"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => shiftDay(1)}
          aria-label="Ngày sau"
        >
          <ChevronRight />
        </Button>
        <Button type="button" variant="outline" onClick={() => setDate(todayIso())}>
          Hôm nay
        </Button>

        <div className="inline-flex rounded-md border p-0.5">
          {(
            [
              { key: "ALL", label: "Tất cả" },
              { key: "EXAM", label: "Khám" },
              { key: "VAX", label: "Tiêm" },
            ] as { key: TypeFilter; label: string }[]
          ).map((opt) => (
            <Button
              key={opt.key}
              type="button"
              size="sm"
              variant={typeFilter === opt.key ? "default" : "ghost"}
              onClick={() => set({ type: opt.key })}
            >
              {opt.label}
            </Button>
          ))}
        </div>

        <div className="text-sm text-muted-foreground">
          {examCount} khám · {vaxCount} tiêm
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Giờ</TableHead>
              <TableHead className="w-20">Loại</TableHead>
              <TableHead>Bệnh nhân</TableHead>
              <TableHead>Nội dung</TableHead>
              <TableHead>Trạng thái</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            ) : visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  {emptyLabel}
                </TableCell>
              </TableRow>
            ) : (
              pagedRows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono tabular-nums">{r.time ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={r.kind === "VAX" ? "outline" : "secondary"}>
                      {r.kind === "VAX" ? "Tiêm" : "Khám"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {r.href ? (
                      <Link to={r.href} className="text-primary hover:underline">
                        {r.patientName}
                      </Link>
                    ) : (
                      r.patientName
                    )}
                  </TableCell>
                  <TableCell>{r.detail}</TableCell>
                  <TableCell>
                    <Badge variant={r.statusVariant}>{r.statusLabel}</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {visibleRows.length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, visibleRows.length)} / {visibleRows.length}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => set({ page: Math.max(1, currentPage - 1) })}
            >
              <ChevronLeft />
              Trước
            </Button>
            <span className="tabular-nums">
              Trang {currentPage} / {pageCount}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage >= pageCount}
              onClick={() => set({ page: Math.min(pageCount, currentPage + 1) })}
            >
              Sau
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
